import React from 'react';
import {
  Grid,
  Box,
  Layout,
  Tag,
  Spacer,
  Text,
  Button,
} from '@flexisaf/flexibull2';
import Theme from 'utils/theme';
import { calculateDateOffset, stringToHslColor } from 'utils/helpers';

import { FormWrapper } from './styles';
import { getColumnStructure, formatAddress } from 'utils/processFormUtil';
import { useSelector } from 'react-redux';
import { selectInstitution } from 'redux/configurations/selectors';
import { FALL_BACK_TZ } from 'utils/constants';

const moment = require('moment-timezone');

export const FormPreview = ({
  onUpdateForm,
  onSubmitForm,
  processedForm,
  submissionData,
  application,
  courseId,
  processedFormAttachments,
  saving = false,
  schema,
}) => {
  const institution = useSelector(selectInstitution);
  const getCourseDetails = () => {
    const course = application.courses?.find((c) => c.id === courseId);
    return `${course?.title} - ${course?.code}`;
  };

  const filterProcessForm = () => {
    const joinedForm = processedForm.map((form, i) => ({
      ...form,
      ...schema?.layout?.sections[i],
    }));
    const filteredForm = joinedForm.filter(
      (form) => form?.refereeSectionType?.toLowerCase() !== 'referee'
    );
    return filteredForm;
  };

  return (
    <Layout>
      <FormWrapper>
        <Box pad="30px 40px" background="#fff">
          <Box margin="10px auto 30px" align="center" vAlign>
            <Text
              bold
              block
              size="25px"
              color={({ theme }) => theme.PrimaryColor}
            >
              Review form
            </Text>
          </Box>
          <Grid default="1fr max-content" gap="20px" templateRows="1fr auto">
            <Grid default="auto max-content" gap="20px">
              <Box>
                <Text bold block size="20px">
                  {submissionData?.first_name} {submissionData?.last_name}
                </Text>
                <Spacer space="10px" />
              </Box>
            </Grid>
            <Box style={{ gridColumn: '1/3' }}>
              <Spacer space="5px" />
              <Box margin="0 40px 0 0">
                <Text size="14px" capitalize color={Theme.PrimaryColor} bold>
                  {application?.title}
                </Text>
              </Box>
              <Box margin="10px 40px 0 0">
                <Text bold uppercase>
                  Course:
                </Text>{' '}
                <Text uppercase>{getCourseDetails()}</Text>
              </Box>
              <Box margin="10px 0 0 0">
                {application?.applicationGroupItems?.map((group, i) => (
                  <Tag
                    key={i}
                    height={25}
                    font="12px"
                    style={{ border: 'none' }}
                    color={stringToHslColor(group.name, 100, 30)}
                    fontColor={stringToHslColor(group.name, 30, 40)}
                    spaceRight
                  >
                    {group.name}
                  </Tag>
                ))}
              </Box>
            </Box>
          </Grid>
        </Box>
        <Box pad="0 10px 10px 40px" className="view-application-box">
          <>
            <Box margin=" 0 30px 0 0">
              <Grid default="1fr 260px">
                <Box />
                {submissionData?.passport_picture && (
                  <Box
                    background="#fff"
                    margin="auto 0 -15.8rem auto"
                    pad="8px"
                    style={{ border: '1px solid #D8D8E3', zIndex: 2 }}
                  >
                    <img
                      width="250px"
                      src={submissionData?.passport_picture}
                      alt="Passport"
                    />
                  </Box>
                )}
              </Grid>
            </Box>
            <Box className="applicant-info">
              {filterProcessForm()?.map((item, i) => (
                <Box margin="40px 0 0" pad="20px 0 0" key={i}>
                  <Text block bold capitalize size="16px" uppercase>
                    {item.title}
                  </Text>

                  <Spacer space="30px" />
                  {item?.inputs?.map((eachRow, rowIndex) => (
                    <Grid
                      className="form-grid"
                      default={getColumnStructure(eachRow)}
                      gap="0px"
                      key={`row-${rowIndex}`}
                    >
                      {eachRow?.map((eachField, i) => (
                        <Box
                          key={i}
                          pad="20px 30px 20px 0"
                          className="single-form"
                        >
                          <Text
                            color={({ theme }) => theme.PrimaryInputOutline}
                            uppercase
                            size="13px"
                            bold
                            block
                          >
                            {eachField.title}
                          </Text>
                          <Spacer space="15px" />
                          {eachField?.source === 'nigeria-states-lga' ? (
                            <Text capitalize size="15px" block>
                              {formatAddress(submissionData?.[eachField.id])}
                            </Text>
                          ) : (
                            <Text
                              capitalize={
                                !['email', 'wysiwyg'].includes(
                                  eachField.type?.toLowerCase()
                                )
                              }
                              size="15px"
                              block
                            >
                              {eachField?.type === 'checkbox' ? (
                                <>
                                  {submissionData?.[eachField.id]
                                    ? eachField.trueDisplay
                                    : eachField.falseDisplay}
                                </>
                              ) : (
                                <>
                                  {['date', 'single-date'].includes(
                                    eachField.type?.toLowerCase()
                                  ) && submissionData?.[eachField.id] ? (
                                    moment(
                                      calculateDateOffset(
                                        submissionData?.[eachField.id],
                                        institution?.timeZone
                                      )
                                    )
                                      .tz(institution?.timeZone || FALL_BACK_TZ)
                                      .format('MMMM Do YYYY')
                                  ) : (
                                    <p
                                      dangerouslySetInnerHTML={{
                                        __html: submissionData?.[eachField.id],
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Grid>
                  ))}
                </Box>
              ))}

              {processedFormAttachments?.find(
                (eachAttachment) =>
                  submissionData?.[eachAttachment.id]?.[0]?.name
              ) && (
                <Box margin="40px 0 0" pad="20px 0 0">
                  <Text block bold capitalize size="16px" uppercase>
                    Attachments <i className="safapply-attach"></i>
                  </Text>

                  <Spacer space="30px" />
                  <Grid default="1fr 1fr 1fr" gap="0px">
                    {processedFormAttachments?.map((eachAttachment, i) => (
                      <Box key={i} pad="20px 30px 20px 0">
                        <Text
                          color={({ theme }) => theme.PrimaryInputOutline}
                          uppercase
                          size="13px"
                          bold
                          block
                        >
                          {eachAttachment.alternateLabel}
                        </Text>
                        <Spacer space="15px" />
                        <Text
                          capitalize={
                            !!String(eachAttachment?.type)?.toLowerCase() ===
                            'email'
                          }
                          size="15px"
                          block
                        >
                          {submissionData?.[eachAttachment.id]?.[0]?.name}
                        </Text>
                      </Box>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </>
        </Box>
        <Box margin="20px 30px 70px" align="right" className="form-actions">
          <Button
            height={50}
            pale
            color="#ccc"
            width={160}
            spaceRight="40px"
            fontColor={({ theme }) => theme.PrimaryInputOutline}
            iconLeft={<i className="saf-left-open"></i>}
            onClick={onUpdateForm}
          >
            Edit details
          </Button>
          <Button
            height={50}
            bold
            width={140}
            onClick={onSubmitForm}
            progress={saving}
          >
            Update
          </Button>
        </Box>
      </FormWrapper>
    </Layout>
  );
};
